import { removeHtmlTag } from '@exam-management/common/utils';
import { QBankStructureLevelCategory } from '@exam-management/qbs-provider';
import { z } from 'zod';

const MIN_P_VALUE = 0;
const MAX_P_VALUE = 1;

const SearchSelectionSchema = z
  .object({
    value: z.string(),
    label: z.string(),
  })
  .nullish();

const ConvertEmptyStringToUndefinedSchema = z
  .string()
  .optional()
  .transform((value) => {
    if (value === '') return undefined;
    return value;
  });

export const ItemSetSubQuestionEditFormSchema = z.object({
  location: z.object({
    [QBankStructureLevelCategory.Part]: z
      .object({
        value: z.string(),
        label: z.string(),
      })
      .nullish(),
    [QBankStructureLevelCategory.Subject]: z
      .object({
        value: z.string(),
        label: z.string(),
      })
      .nullish(),
    [QBankStructureLevelCategory.Group]: z
      .object({
        value: z.string(),
        label: z.string(),
      })
      .nullish(),
    [QBankStructureLevelCategory.Chapter]: z
      .object({
        value: z.string(),
        label: z.string(),
      })
      .nullish(),
    [QBankStructureLevelCategory.Topic]: z
      .object({
        value: z.string(),
        label: z.string(),
      })
      .nullish(),
    [QBankStructureLevelCategory.SubTopic]: z
      .object({
        value: z.string(),
        label: z.string(),
      })
      .nullish(),
  }),
  information: z.object({
    calculated: z.string().optional(),
    pStats: z
      .array(
        z.object({
          selected: z.boolean().nullable(),
          firstUsedAt: z.preprocess(
            (arg) => (typeof arg == 'string' || arg instanceof Date ? new Date(arg) : arg),
            z.date().nullable(),
          ),
          pValue: z.number(),
          sampleSize: z.number().int().nonnegative(),
          calculatedAt: z.preprocess(
            (arg) => (typeof arg == 'string' || arg instanceof Date ? new Date(arg) : arg),
            z.date(),
          ),
          updatedBy: z.string().uuid(),
          updatedUserFullName: z.string(),
        }),
      )
      .nullish(),
    pValue: z.object({
      selected: z.enum(['pInitial', 'pStat']),
      pInitial: z
        .union([
          z
            .number({
              message: '',
            })
            .min(MIN_P_VALUE, {
              message: `เกินค่าต่ำสุด (${MIN_P_VALUE})`,
            })
            .max(MAX_P_VALUE, {
              message: `เกินค่าสูงสุด (${MAX_P_VALUE})`,
            }),
          z.literal(''),
        ])
        .nullish()
        .transform((value) => {
          if (value === '') {
            return undefined;
          }
          return value;
        }),
      pStat: z.number().optional(),
    }),
    bIndex: z.preprocess((value) => {
      if (value === '') {
        return null;
      }
      return value;
    }, z.number().nullish()),
  }),
  detail: z.object({
    details: z.string().refine(
      (value) => {
        const details = removeHtmlTag(value, {
          customTag: 'p',
        }).trim();
        return details.length >= 1;
      },
      {
        message: 'กรุณากรอกรายละเอียดคำถาม',
      },
    ),
    choices: z
      .array(
        z.object({
          value: z.string(),
          label: z
            .string()
            .trim()
            .refine(
              (value) => {
                const label = removeHtmlTag(value, {
                  customTag: 'p',
                }).trim();
                return label.length >= 1;
              },
              {
                message: 'กรุณากรอกรายละเอียดคำตอบ',
              },
            ),
          order: z.number(),
        }),
      )
      .min(2, 'กรุณากรอกตัวเลือกอย่างน้อย 2 ข้อ'),
    correctAnswer: z.object({
      value: z.string().min(1, 'กรุณาเลือกคำตอบที่ถูกต้อง'),
    }),
  }),
  properties: z.object({
    cognitiveDomain: z.array(SearchSelectionSchema).optional(),
    learningObjectiveStatement: SearchSelectionSchema,
    solution: z.string().optional(),
    reference: z.string().optional(),
  }),
  owners: z.object({
    owner: ConvertEmptyStringToUndefinedSchema,
    audit1: ConvertEmptyStringToUndefinedSchema,
    audit2: ConvertEmptyStringToUndefinedSchema,
    audit3: ConvertEmptyStringToUndefinedSchema,
  }),
});
